import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, createApiClient } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import {
  Plus,
  MagnifyingGlass,
  ForkKnife,
  EnvelopeSimple,
  SpinnerGap,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import RestaurantCard from '../components/RestaurantCard';
import AddRestaurantModal from '../components/AddRestaurantModal';
import ScanBillModal from '../components/ScanBillModal';
import ShareCardModal from '../components/ShareCardModal';
import PhotoVisitModal from '../components/PhotoVisitModal';
import MenuScanModal from '../components/MenuScanModal';
import UpcomingVisitsBanner from '../components/dashboard/UpcomingVisitsBanner';
import MemoriesSection from '../components/dashboard/MemoriesSection';
import RecommendationsSection from '../components/dashboard/RecommendationsSection';
import EmailBookingsSection from '../components/dashboard/EmailBookingsSection';

const Dashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const api = createApiClient(token);

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showScanBillModal, setShowScanBillModal] = useState(false);
  const [showPhotoVisitModal, setShowPhotoVisitModal] = useState(false);
  const [showMenuScanModal, setShowMenuScanModal] = useState(false);
  const [shareRestaurant, setShareRestaurant] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [scanningEmails, setScanningEmails] = useState(false);
  const [emailBookings, setEmailBookings] = useState([]);
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [memories, setMemories] = useState([]);

  useEffect(() => {
    fetchRestaurants();
  }, [activeTab]);

  useEffect(() => {
    fetchRecommendations();
    checkGmailStatus();
    checkUpcomingVisits();
    fetchMemories();
  }, []);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') {
        params.state = activeTab;
      }
      const response = await api.get('/restaurants', { params });
      setRestaurants(response.data.restaurants || response.data.items || []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      toast.error('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const handleRestaurantAdded = (restaurant) => {
    setRestaurants(prev => [restaurant, ...prev]);
    setShowAddModal(false);
    toast.success(`Added ${restaurant.name} to your list!`);
  };

  const fetchRecommendations = async () => {
    try {
      const res = await api.get('/recommendations?limit=6');
      setRecommendations(res.data.recommendations || []);
    } catch (error) {
      console.error('Recommendations error:', error);
    }
  };

  const checkGmailStatus = async () => {
    try {
      const res = await api.get('/gmail/status');
      setGmailConnected(res.data.connected);
    } catch (error) {
      console.error('Gmail status error:', error);
    }
  };

  const handleConnectGmail = async () => {
    try {
      const res = await api.get('/oauth/gmail/login');
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error('Failed to start Gmail connection');
    }
  };

  const handleScanEmails = async () => {
    setScanningEmails(true);
    try {
      const res = await api.post('/bookings/scan-emails');
      setEmailBookings(res.data.bookings || []);
      if (res.data.bookings?.length > 0) {
        toast.success(`Found ${res.data.bookings.length} booking email(s)!`);
      } else {
        toast.info('No booking emails found in the last 90 days');
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes('not connected')) {
        toast.error('Please connect your Gmail account first');
      } else {
        toast.error('Failed to scan emails');
      }
    } finally {
      setScanningEmails(false);
    }
  };

  const handleImportBooking = async (booking) => {
    try {
      if (booking.matched_restaurant) {
        await api.post(`/restaurants/${booking.matched_restaurant.id}/plan`, {
          planned_visit_at: `${booking.booking_date}T${booking.booking_time || '19:00'}:00`,
          booking_party_size: booking.party_size || 2,
          notes: booking.notes || `Booking ${booking.confirmation_number || ''}`.trim()
        });
        toast.success(`Planned visit to ${booking.matched_restaurant.name}!`);
      } else {
        const newR = await api.post('/restaurants', {
          name: booking.restaurant_name,
          state: 'planned'
        });
        if (booking.booking_date) {
          await api.post(`/restaurants/${newR.data.id}/plan`, {
            planned_visit_at: `${booking.booking_date}T${booking.booking_time || '19:00'}:00`,
            booking_party_size: booking.party_size || 2,
          });
        }
        toast.success(`Added ${booking.restaurant_name} and planned visit!`);
      }
      setEmailBookings(prev => prev.filter(b => b.email_id !== booking.email_id));
      fetchRestaurants();
    } catch (error) {
      toast.error('Failed to import booking');
    }
  };

  const checkUpcomingVisits = async () => {
    try {
      const res = await api.get('/notifications/upcoming');
      const upcoming = res.data.upcoming || [];
      setUpcomingVisits(upcoming);

      if (upcoming.length > 0 && 'Notification' in window) {
        if (Notification.permission === 'default') {
          await Notification.requestPermission();
        }
        if (Notification.permission === 'granted') {
          const notifiedKey = `notified_visits_${new Date().toISOString().split('T')[0]}`;
          const alreadyNotified = JSON.parse(localStorage.getItem(notifiedKey) || '[]');

          upcoming.forEach(visit => {
            if (!alreadyNotified.includes(visit.restaurant_id)) {
              const timeStr = visit.hours_until < 1
                ? 'less than an hour'
                : `${Math.round(visit.hours_until)} hours`;
              new Notification(`Upcoming: ${visit.name}`, {
                body: `Your visit is in ${timeStr}${visit.suburb ? ` · ${visit.suburb}` : ''}${visit.party_size ? ` · Party of ${visit.party_size}` : ''}`,
                icon: visit.hero_image_url || '/favicon.ico',
                tag: `visit-${visit.restaurant_id}`,
              });
              alreadyNotified.push(visit.restaurant_id);
            }
          });

          localStorage.setItem(notifiedKey, JSON.stringify(alreadyNotified));
        }
      }
    } catch (error) {
      console.error('Upcoming visits error:', error);
    }
  };

  const fetchMemories = async () => {
    try {
      const res = await api.get('/memories');
      setMemories(res.data.memories || []);
    } catch (error) {
      console.error('Memories error:', error);
    }
  };

  const handleDeleteRestaurant = async (id) => {
    try {
      await api.delete(`/restaurants/${id}`);
      setRestaurants(prev => prev.filter(r => r.id !== id));
      toast.success('Restaurant deleted');
    } catch (error) {
      toast.error('Failed to delete restaurant');
    }
  };

  const handleTransition = async (id, newState) => {
    try {
      await api.post(`/restaurants/${id}/transition`, { state: newState });
      fetchRestaurants();
      toast.success(`Moved to ${newState.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Failed to update restaurant');
    }
  };

  const filteredRestaurants = restaurants.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.suburb?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.cuisine?.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getCounts = () => {
    const all = restaurants.length;
    const wantToTry = restaurants.filter(r => r.state === 'want_to_try').length;
    const planned = restaurants.filter(r => r.state === 'planned').length;
    const visited = restaurants.filter(r => r.state === 'visited').length;
    return { all, wantToTry, planned, visited };
  };

  const counts = getCounts();

  return (
    <div className="container-app py-8 pb-8">
      <SEO title="My Restaurants" description="Your personal dining dashboard — track visits, ratings, and dining history across Cape Town." path="/" noindex />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1A1918]" data-testid="dashboard-title">
            My Restaurants
          </h1>
          <p className="text-[#7A7873] mt-1">Track your dining adventures across Cape Town</p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {gmailConnected ? (
            <Button
              onClick={handleScanEmails}
              variant="outline"
              disabled={scanningEmails}
              className="flex items-center gap-2 border-[#4B5945] text-[#4B5945] hover:bg-[#4B5945]/5"
              data-testid="scan-emails-btn"
            >
              {scanningEmails ? (
                <SpinnerGap className="w-5 h-5 animate-spin" />
              ) : (
                <EnvelopeSimple weight="bold" className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">{scanningEmails ? 'Scanning...' : 'Scan Emails'}</span>
            </Button>
          ) : (
            <Button
              onClick={handleConnectGmail}
              variant="outline"
              className="flex items-center gap-2 border-[#4B5945] text-[#4B5945] hover:bg-[#4B5945]/5"
              data-testid="connect-gmail-btn"
            >
              <EnvelopeSimple weight="bold" className="w-5 h-5" />
              <span className="hidden sm:inline">Connect Gmail</span>
            </Button>
          )}
          <Button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-restaurant-btn"
          >
            <Plus weight="bold" className="w-5 h-5" />
            Add Restaurant
          </Button>
        </div>
      </div>

      {/* Upcoming Visits */}
      <UpcomingVisitsBanner upcomingVisits={upcomingVisits} />

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="search-input-wrapper flex-1">
          <MagnifyingGlass className="search-icon" weight="duotone" />
          <Input
            type="text"
            placeholder="Search restaurants, suburbs, cuisines..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
        <TabsList className="bg-white border border-[#EAE8E3] p-1 rounded-full">
          <TabsTrigger value="all" className="rounded-full data-[state=active]:bg-[#A94438] data-[state=active]:text-white" data-testid="tab-all">
            All ({counts.all})
          </TabsTrigger>
          <TabsTrigger value="want_to_try" className="rounded-full data-[state=active]:bg-[#E8DCD0] data-[state=active]:text-[#1A1918]" data-testid="tab-want-to-try">
            Want to Try ({counts.wantToTry})
          </TabsTrigger>
          <TabsTrigger value="planned" className="rounded-full data-[state=active]:bg-[#D1E2C4] data-[state=active]:text-[#1A1918]" data-testid="tab-planned">
            Planned ({counts.planned})
          </TabsTrigger>
          <TabsTrigger value="visited" className="rounded-full data-[state=active]:bg-[#4B5945] data-[state=active]:text-white" data-testid="tab-visited">
            Visited ({counts.visited})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Recommendations */}
      <RecommendationsSection recommendations={recommendations} />

      {/* Memories */}
      <MemoriesSection memories={memories} />

      {/* Email Bookings */}
      <EmailBookingsSection emailBookings={emailBookings} onImportBooking={handleImportBooking} />

      {/* Restaurant Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card-elevated animate-pulse">
              <div className="h-48 bg-[#E8DCD0] rounded-t-xl" />
              <div className="p-6 space-y-3">
                <div className="h-6 bg-[#E8DCD0] rounded w-3/4" />
                <div className="h-4 bg-[#E8DCD0] rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredRestaurants.length === 0 ? (
        <div className="empty-state">
          <ForkKnife weight="duotone" className="empty-state-icon" />
          <h3 className="empty-state-title">
            {searchQuery ? 'No restaurants found' : 'No restaurants yet'}
          </h3>
          <p className="empty-state-text">
            {searchQuery ? 'Try a different search term' : 'Start by adding restaurants you want to try'}
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowAddModal(true)} className="btn-primary mt-6" data-testid="empty-add-btn">
              <Plus weight="bold" className="w-5 h-5 mr-2" />
              Add your first restaurant
            </Button>
          )}
        </div>
      ) : (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
          }}
        >
          <AnimatePresence>
            {filteredRestaurants.map((restaurant) => (
              <motion.div
                key={restaurant.id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                exit={{ opacity: 0, scale: 0.9 }}
                layout
              >
                <RestaurantCard
                  restaurant={restaurant}
                  onDelete={() => handleDeleteRestaurant(restaurant.id)}
                  onTransition={(state) => handleTransition(restaurant.id, state)}
                  onClick={() => navigate(`/restaurants/${restaurant.id}`)}
                  onShare={() => setShareRestaurant(restaurant)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Modals */}
      <AddRestaurantModal open={showAddModal} onOpenChange={setShowAddModal} onSuccess={handleRestaurantAdded} />
      <ScanBillModal open={showScanBillModal} onOpenChange={setShowScanBillModal} onVisitCreated={() => fetchRestaurants()} restaurants={restaurants} />
      <ShareCardModal open={!!shareRestaurant} onOpenChange={(open) => { if (!open) setShareRestaurant(null); }} restaurant={shareRestaurant} />
      <PhotoVisitModal open={showPhotoVisitModal} onOpenChange={setShowPhotoVisitModal} onVisitCreated={() => fetchRestaurants()} />
      <MenuScanModal open={showMenuScanModal} onOpenChange={setShowMenuScanModal} />
    </div>
  );
};

export default Dashboard;
